/**
 * Planning a next step FROM the calendar, where the day is known and the party is not.
 *
 * Everywhere else in the CRM a step is planned from a record: the lead row and
 * the client header both already know who the step is about, and `NextStepDialog`
 * is handed a bound `onSave` for exactly that reason. The calendar is the one
 * screen that arrives from the other direction - the operator is looking at a
 * Thursday with nothing on it and wants to put something there.
 *
 * So this file owns the half the shared dialog deliberately does not: which
 * company, and therefore which of the two routes the entry goes to. `PartyPicker`
 * already answers "a registered client OR a juhuklient" for tickets and calls,
 * and reusing it keeps a third autocomplete with third manners out of the CRM.
 *
 * The submit button stays dead until a party is PICKED, not merely typed. A name
 * in the box is a draft; the route wants an id, and a form that let the operator
 * press save on a typed name would fail after the work rather than before it.
 */
import { useState } from "react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { addClientActivity, addLeadActivity, type Activity } from "../../../lib/clients";
import { defaultStepAt } from "../../../lib/nextStep";
import { NextStepDialog } from "../_shared/NextStep";
import { PartyPicker, type PartyRef } from "../_shared/PartyPicker";

export function PlanStepDialog({
  day,
  onClose,
  onPlanned,
}: {
  /** The cell that was clicked. Only its date is used; the step lands at 09:00. */
  day: Date;
  onClose: () => void;
  /** Fired once the entry exists, so the month can be re-read. */
  onPlanned: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [typed, setTyped] = useState("");
  const [party, setParty] = useState<PartyRef | null>(null);

  // The dialog's subtitle line says what it is ABOUT. For the record-bound
  // callers that is the company; here it is the day, because the day is the one
  // thing the operator already decided by clicking. Repeating the action there
  // would print "Plan the next step" twice, once as the heading and once under
  // it.
  const dayLabel = (() => {
    try {
      return new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(day);
    } catch {
      return day.toLocaleDateString();
    }
  })();

  // Nine in the morning ON the clicked day: `defaultStepAt` counts days from a
  // reference instant, so zero days from that day is that day. The hour is the
  // same one every other planned step gets, and it is what makes the new entry
  // sort ahead of an afternoon task rather than sitting at midnight.
  const initialAt = defaultStepAt(0, day);

  async function save(body: { type: string; body?: string; dueAt: string }): Promise<Activity> {
    // Unreachable while `canSave` is false; here so the promise type holds
    // without a non-null assertion that a later edit could make untrue.
    if (!party) throw new Error("no party");
    return party.kind === "client"
      ? addClientActivity(party.id, body)
      : addLeadActivity(party.id, body);
  }

  return (
    <NextStepDialog
      title={dayLabel}
      initialAt={initialAt}
      canSave={!!party}
      onClose={onClose}
      onSave={save}
      onSaved={() => {
        onPlanned();
        onClose();
      }}
    >
      <div>
        <span className="crm-lab">{t("calendar.planFor")}</span>
        <PartyPicker
          value={typed}
          selected={party}
          onChange={(v) => {
            setTyped(v);
            // Editing the name after picking detaches it. Without this the form
            // would keep saving to the company whose name has been typed over.
            if (party && v !== party.name) setParty(null);
          }}
          onPick={setParty}
          placeholder={t("calendar.planForPlaceholder")}
          required
        />
      </div>
    </NextStepDialog>
  );
}
